import React, { useMemo, KeyboardEvent, useEffect, ChangeEvent, useRef, useState } from 'react';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { getSiteKey } from '../../lib/utils/common-functions';
import useInitialErrors from '../../hooks/formik-initial-errors';
import { emailRegexp, passwordRegExp } from '../../lib/utils/validation';
import getValue from 'lodash/get';
import { object as objectYup, string as stringYup, ref as refYup } from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { userOtpService } from '../../lib/api/userOtpValidate';
import { AccountSetupType, OTPType, OtpValidationType, validateOTPFormType } from '../../types/userOtp';
import { customAuthService } from '../../lib/api/customAuth';
import { ForgotPasswordType } from '../../types/customAuth';
import Swal from 'sweetalert2';
import { Message, SuccessIcon, SweetAlertTitle, WarningIcon } from '../../lib/constants/messages';
import { history } from '../../history';
import { isCaptchaEnabled } from '../../config/constants';

type Props = {
    setShowResetPassTab: (key: boolean) => void;
};

const initialFormData = {
    email: '',
    password: '',
    recaptcha: '',
    cpassword: '',
};

const ResetPassword: React.FC<Props> = ({ setShowResetPassTab }) => {
    const [showStep, setShowStep] = useState('1');
    const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
    const otpFields = useRef<HTMLInputElement[]>([]);
    const [filledFields, setFilledFields] = useState<number[]>([]);
    const [concatenatedOtp, setConcatenatedOtp] = useState('');
    const [otpVerified, setOtpVerified] = useState(false);
    const [isOTPNotValidated, setIsOTPNotValidated] = useState(false);
    const [removeSentMessage, setRemoveSentMessage] = useState(true);
    const [disableResetButton, setDisableResetButton] = useState(false);
    const [timer, setTimer] = useState<number>(30);
    const [otpExpired, setOtpExpired] = useState(false);
    const [showCpass, setShowCpass] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [loadingSpin, setLoadingSpin] = useState(false);
    const [base64PassString, setBase64PassString] = useState('');
    const [customPass, setCustomPass] = useState('');
    const [renderKey, setRenderKey] = useState(false);
    const [disableResetPassBtn, setDisableResetPassBtn] = useState(true);

    const captchaEnabled = String(isCaptchaEnabled).toLowerCase() === 'true' ? true : false;

    // ForgotPasswordType

    const fetchData = async (email: string, otpType: OTPType) => {
        try {
            setLoadingSpin(true);

            const path = history.location.pathname;

            const {
                resultObject: { otpValidateType },
            } =
                path !== '/api-integrate'
                    ? await userOtpService.updateUserOtp({ email, otpType })
                    : await userOtpService.updateHRMSUserOtp({ email, otpType });

            if (otpValidateType === OtpValidationType.Valid) {
                setShowStep('2');
                setLoadingSpin(false);
                return 1;
            } else {
                setLoadingSpin(false);
            }
        } catch (err) {
            // setError('An error occurred while fetching data.');
            setLoadingSpin(false);
        }
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
        const sanitizedValue = e.target.value.replace(/[^0-9]/g, '');
        const newOtp = [...otp];
        newOtp[index] = sanitizedValue;
        setOtp(newOtp);

        if (index < 5 && sanitizedValue !== '') {
            otpFields.current[index + 1].focus();
            setOtpVerified(false);
        }

        const newFilledFields = newOtp.map((digit, i) => (digit ? i : -1)).filter((digit) => digit !== -1);
        setFilledFields(newFilledFields);

        const concatenatedValue = newOtp.join('');
        setConcatenatedOtp(concatenatedValue);

        const anyFieldFilled = newFilledFields.length > 0;

        if (anyFieldFilled) {
            setRemoveSentMessage(false);
        }
    };

    const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && index > 0 && otp[index] === '') {
            otpFields.current[index - 1].focus();
        }

        if (e.key === 'Backspace' || e.key === 'Delete') {
            setOtpExpired(false);
            setIsOTPNotValidated(false);
        }
    };

    const handleVerifyOTP = async (data: validateOTPFormType) => {
        try {
            setLoadingSpin(true);

            const path = history.location.pathname;

            const {
                resultObject: { otpValidateType },
            } =
                path !== '/api-integrate'
                    ? await userOtpService.validateOTP(data)
                    : await userOtpService.validateHRMSOTP(data);

            if (otpValidateType === OtpValidationType.Valid) {
                setOtpVerified(true);
                setTimeout(() => {
                    setLoadingSpin(false);
                    setShowStep('3');
                }, 1000);
            } else if (otpValidateType === OtpValidationType.InValid) {
                setOtpVerified(false);
                setIsOTPNotValidated(true);
                setRemoveSentMessage(false);
                setLoadingSpin(false);
            } else if (otpValidateType === OtpValidationType.Expired) {
                setOtpExpired(true);
                setLoadingSpin(false);
            } else {
                setOtpVerified(false);
                setLoadingSpin(false);
            }
        } catch (err) {
            console.log(err, 'OTP Error');
            setLoadingSpin(false);
        }
    };

    const resetPassword = async (data: ForgotPasswordType) => {
        try {
            setLoadingSpin(true);
            const path = history.location.pathname;
            const result =
                path !== '/api-integrate'
                    ? await customAuthService.getForgotPassword(data)
                    : await userOtpService.getHRMSForgotPassword(data);

            // const result = await customAuthService.getForgotPassword(data);

            setTimeout(() => {
                setLoadingSpin(false);
                Swal.fire({
                    icon: SuccessIcon,
                    text: Message.PasswordReset,
                }).then(() => {
                    setShowResetPassTab(false);
                });
            }, 2000);
        } catch (error: any) {
            if (error.response.status === 400) {
                if (error.response.data.error.message === Message.CustomAuthUnAvailable) {
                    Swal.fire({
                        title: SweetAlertTitle.Sorry,
                        icon: WarningIcon,
                        confirmButtonText: 'Ok',
                        text: Message.RegisteredWithGoogle,
                    });
                }
            }
            setLoadingSpin(false);
        }
    };

    const resetOtp = () => {
        setRemoveSentMessage(true);
        setOtpExpired(false);
        setIsOTPNotValidated(false);
        setOtp(Array(6).fill(''));
        setFilledFields([]);
        setConcatenatedOtp('');
        setOtpVerified(false);
    };

    const profileFormData = useMemo(() => {}, []);

    const initialErrors = useInitialErrors(profileFormData, getValidationSchema(captchaEnabled));

    useEffect(() => {
        if (disableResetButton) {
            setTimeout(() => {
                setDisableResetButton(false);
            }, 30000);
        }
    }, [disableResetButton]);

    useEffect(() => {
        let intervalId: NodeJS.Timeout;

        const decrementTimer = () => {
            setTimer((prevTimer) => prevTimer - 1);
        };

        intervalId = setInterval(decrementTimer, 1000);

        if (timer === 0) {
            clearInterval(intervalId);
            setDisableResetButton(false);
        }

        return () => {
            clearInterval(intervalId);
        };
    }, [timer]);

    useEffect(() => {
        if (otpVerified) {
            setIsOTPNotValidated(false);
            setRemoveSentMessage(false);
            setOtpExpired(false);
        }
    }, [otpVerified]);

    useEffect(() => {
        if (customPass) {
            const convertToBase64 = () => {
                const encodedString = btoa(customPass);
                setBase64PassString(encodedString);
            };

            convertToBase64();
        }
    }, [customPass]);

    return (
        <div>
            <Formik
                onSubmit={() => {}}
                enableReinitialize
                initialValues={initialFormData}
                validationSchema={getValidationSchema(captchaEnabled)}
                initialErrors={initialErrors}
            >
                {(formikProps: FormikProps<any>) => {
                    const { handleSubmit, values, setFieldTouched, setFieldValue } = formikProps;

                    const siteKey = getSiteKey();

                    return (
                        <form onSubmit={handleSubmit}>
                            <>
                                {loadingSpin && (
                                    <div className="new-spinner p-absolute">
                                        <div>
                                            <span className="spinner-border spinner-border-sm custom-spinner-border" />
                                        </div>
                                        <p className="fs-14 custom-loading-text">Loading</p>
                                    </div>
                                )}
                                {showStep === '1' ? (
                                    <div className="mx-auto mt-5" style={{ maxWidth: '600px' }}>
                                        <div className="card lt-shadow-sm">
                                            <div className="login-card-body">
                                                <div className="w-100 mb-4">
                                                    <h1 className="fs-28 fw-600 mb-2">Forgot Password?</h1>
                                                    <div className="fs-14 lt-text-secondary">
                                                        We Will Send You An OTP To Your Email
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="fs-14 fw-400 mb-2" htmlFor="email">
                                                        Email <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="email">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);

                                                            return (
                                                                <>
                                                                    <input
                                                                        type="email"
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            setFieldTouched(field.name);
                                                                            setFieldValue(field.name, ev.target.value);
                                                                        }}
                                                                        className="form-control h-44"
                                                                        placeholder="Enter Email"
                                                                        id="email"
                                                                    />
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                                {captchaEnabled && (
                                                    <div className="mb-3">
                                                        <Field name="recaptcha" key={renderKey}>
                                                            {(fieldProps: FieldProps) => {
                                                                const { field, form } = fieldProps;
                                                                const error =
                                                                    getValue(form.touched, field.name) &&
                                                                    getValue(form.errors, field.name);
                                                                return (
                                                                    <div>
                                                                        <ReCAPTCHA
                                                                            sitekey={siteKey}
                                                                            onChange={(value) => {
                                                                                setFieldTouched(field.name);
                                                                                setFieldValue(field.name, value);
                                                                            }}
                                                                        />
                                                                        {typeof error === 'string' && (
                                                                            <small className="text-danger fs-12">
                                                                                {error}
                                                                            </small>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }}
                                                        </Field>
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const shouldSubmit = captchaEnabled
                                                            ? Boolean(values.recaptcha)
                                                            : true;

                                                        if (values.email) {
                                                            if (shouldSubmit) {
                                                                fetchData(values.email, OTPType.ForgotPassword);

                                                                setTimeout(() => {
                                                                    setRenderKey(!renderKey);
                                                                    setFieldValue('recaptcha', '');
                                                                }, 2000);
                                                            } else {
                                                                setFieldTouched('recaptcha');
                                                            }
                                                        } else {
                                                            setFieldTouched('email');
                                                            setFieldTouched('recaptcha');
                                                        }
                                                    }}
                                                    className="btn btn-primary w-100 h-44"
                                                >
                                                    Get OTP
                                                </button>
                                                <div className="text-center mt-3 cursor-pointer mb-3">
                                                    <i className="bi bi-arrow-left fs-16"></i>{' '}
                                                    <span
                                                        className="fs-14"
                                                        onClick={() => {
                                                            setShowResetPassTab(false);
                                                        }}
                                                    >
                                                        Back To Log In
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : showStep === '2' ? (
                                    <div className="">
                                        <div className="mx-auto d-block" style={{ maxWidth: '600px' }}>
                                            <div className="card lt-shadow-sm">
                                                <div className="login-card-body">
                                                    <div className="w-100 mb-4">
                                                        <h1 className="fs-28 fw-600 mb-2">Verify Code</h1>
                                                        <div className="fs-14 lt-text-secondary">
                                                            We Sent A Code To{' '}
                                                            <span className="fw-600">{values.email}</span>
                                                        </div>
                                                    </div>
                                                    <div className="row mb-3">
                                                        {otp.map((digit, index) => (
                                                            <div className="col-lg-2 col-2">
                                                                <input
                                                                    key={index}
                                                                    type="text"
                                                                    pattern="[0-9]"
                                                                    className="form-control px-1 text-center"
                                                                    maxLength={1}
                                                                    value={digit}
                                                                    onChange={(e) => handleInputChange(e, index)}
                                                                    onKeyDown={(e) => handleInputKeyDown(e, index)}
                                                                    ref={(input) =>
                                                                        (otpFields.current[index] =
                                                                            input as HTMLInputElement)
                                                                    }
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="d-flex justify-content-between mt-1 mb-3">
                                                        <div>
                                                            {!otpExpired ? (
                                                                <>
                                                                    {!removeSentMessage ? (
                                                                        <small
                                                                            className="text-danger fs-12"
                                                                            style={{
                                                                                visibility: `${
                                                                                    isOTPNotValidated && !otpExpired
                                                                                        ? 'visible'
                                                                                        : 'hidden'
                                                                                }`,
                                                                            }}
                                                                        >
                                                                            Invalid OTP.
                                                                        </small>
                                                                    ) : (
                                                                        ''
                                                                    )}
                                                                    {!isOTPNotValidated ? (
                                                                        <small
                                                                            className="text-success fs-12"
                                                                            style={{
                                                                                visibility: `${
                                                                                    removeSentMessage && !otpExpired
                                                                                        ? 'visible'
                                                                                        : 'hidden'
                                                                                }`,
                                                                            }}
                                                                        >
                                                                            An OTP has been sent to the email.
                                                                        </small>
                                                                    ) : (
                                                                        ''
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <small
                                                                    className="text-danger fs-12"
                                                                    style={{
                                                                        visibility: `${
                                                                            otpExpired ? 'visible' : 'hidden'
                                                                        }`,
                                                                    }}
                                                                >
                                                                    OTP has expired.
                                                                </small>
                                                            )}
                                                        </div>
                                                        <div
                                                            onClick={() => {
                                                                if (!disableResetButton) {
                                                                    setDisableResetButton(true);
                                                                    setTimer(30);
                                                                    fetchData(values.email, OTPType.ForgotPassword);
                                                                }
                                                                resetOtp();
                                                            }}
                                                            className="fs-12 lt-text-primary-alt cursor-pointer text-decoration-underline"
                                                        >
                                                            {' '}
                                                            Resend OTP
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (concatenatedOtp) {
                                                                handleVerifyOTP({
                                                                    otp: concatenatedOtp,
                                                                    email: values.email,
                                                                    otpType: OTPType.ForgotPassword,
                                                                });
                                                            } else {
                                                            }
                                                        }}
                                                        disabled={concatenatedOtp.length !== 6 ? true : false}
                                                        className="btn btn-primary w-100 h-44"
                                                    >
                                                        Verify
                                                    </button>
                                                    {/* <div className="fs-14 lt-text-secondary mt-3 text-center">
                                                    Didn't recieve an email?{' '}
                                                </div> */}
                                                    <div className="text-center mt-3 cursor-pointer mb-3">
                                                        <i className="bi bi-arrow-left fs-16"></i>{' '}
                                                        <span
                                                            className="fs-14"
                                                            onClick={() => {
                                                                setShowStep('1');
                                                                resetOtp();
                                                            }}
                                                        >
                                                            Back
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mx-auto" style={{ maxWidth: '600px' }}>
                                        <div className="card lt-shadow-sm">
                                            <div className="login-card-body">
                                                <div className="w-100 mb-4">
                                                    <h1 className="fs-28 fw-600 mb-2">Set New Password</h1>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        New Password <span className="lt-text-error">*</span>
                                                    </label>
                                                    <div className="position-relative">
                                                        <Field name="password">
                                                            {(fieldProps: FieldProps) => {
                                                                const { field, form } = fieldProps;
                                                                const error =
                                                                    getValue(form.touched, field.name) &&
                                                                    getValue(form.errors, field.name);
                                                                return (
                                                                    <>
                                                                        <input
                                                                            type={`${showPass ? 'text' : 'password'}`}
                                                                            // value={field.value}
                                                                            onChange={(ev) => {
                                                                                setFieldTouched(field.name);
                                                                                setFieldValue(
                                                                                    field.name,
                                                                                    ev.target.value
                                                                                );
                                                                            }}
                                                                            className="form-control h-44"
                                                                            placeholder="Enter Password"
                                                                            style={{ paddingRight: '2rem' }}
                                                                            minLength={8}
                                                                            maxLength={16}
                                                                        />

                                                                        <i
                                                                            onClick={() => setShowPass(!showPass)}
                                                                            className={`bi ${
                                                                                showPass ? 'bi-eye' : 'bi-eye-slash'
                                                                            } password-toggle cursor-pointer`}
                                                                        ></i>
                                                                        {typeof error === 'string' && (
                                                                            <small className="text-danger fs-12">
                                                                                {error}
                                                                            </small>
                                                                        )}
                                                                    </>
                                                                );
                                                            }}
                                                        </Field>
                                                    </div>
                                                    <div className="d-flex justify-content-start mt-1">
                                                        <ul className="fs-12 lt-text-disabled mb-0">
                                                            <li className="lt-text-disabled">
                                                                At least one uppercase character.
                                                            </li>
                                                            <li className="lt-text-disabled">
                                                                At least one lowercase character.
                                                            </li>
                                                            <li className="lt-text-disabled">
                                                                At least one special character.
                                                            </li>
                                                            <li className="lt-text-disabled">At least one number.</li>
                                                            <li className="lt-text-disabled">
                                                                Password must be in between 8 - 16 characters.
                                                            </li>
                                                            <li className="lt-text-disabled">
                                                                Password should not match with your old one.
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        Confirm Password <span className="lt-text-error">*</span>
                                                    </label>

                                                    <Field name="cpassword">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);

                                                            if (field.value !== '' && !error) {
                                                                if (values.password.match(passwordRegExp)) {
                                                                    setDisableResetPassBtn(false);
                                                                }
                                                            } else {
                                                                setDisableResetPassBtn(true);
                                                            }

                                                            return (
                                                                <>
                                                                    <div className="position-relative">
                                                                        <input
                                                                            type={`${showCpass ? 'text' : 'password'}`}
                                                                            // value={field.value}
                                                                            onChange={(ev) => {
                                                                                setFieldTouched(field.name);
                                                                                setFieldValue(
                                                                                    field.name,
                                                                                    ev.target.value
                                                                                );
                                                                                setCustomPass(ev.target.value);
                                                                            }}
                                                                            className="form-control h-44"
                                                                            placeholder="Confirm Password"
                                                                            style={{ paddingRight: '2rem' }}
                                                                            minLength={8}
                                                                            maxLength={16}
                                                                        />
                                                                        <i
                                                                            onClick={() => setShowCpass(!showCpass)}
                                                                            className={`bi ${
                                                                                showCpass ? 'bi-eye' : 'bi-eye-slash'
                                                                            } password-toggle cursor-pointer`}
                                                                        ></i>
                                                                    </div>
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                                <button
                                                    type="submit"
                                                    disabled={disableResetPassBtn}
                                                    onClick={() => {
                                                        if (base64PassString) {
                                                            resetPassword({
                                                                email: values.email,
                                                                password: base64PassString,
                                                                accountSetupType: AccountSetupType.ForgotPasword,
                                                            });
                                                        } else {
                                                        }
                                                    }}
                                                    className="btn btn-primary w-100 h-44"
                                                >
                                                    Reset Password
                                                </button>
                                                <div className="text-center mt-3 cursor-pointer mb-3">
                                                    <i className="bi bi-arrow-left fs-16"></i>{' '}
                                                    <span
                                                        className="fs-14"
                                                        onClick={() => {
                                                            setShowResetPassTab(false);
                                                        }}
                                                    >
                                                        Back To Log In
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        </form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default ResetPassword;

const getValidationSchema = (captchaEnabled: boolean) =>
    objectYup().shape({
        recaptcha: captchaEnabled ? stringYup().nullable().required('Captcha is required.') : stringYup().nullable(),
        email: stringYup().required('Email is required.').matches(emailRegexp, {
            message: 'Invalid email format.',
        }),
        password: stringYup()
            .required('Password is required.')
            .min(8, 'Password must be in between 8 - 16 characters.')
            .matches(passwordRegExp, 'Password must match the below conditions.'),
        cpassword: stringYup()
            .nullable()
            .oneOf([refYup('password'), null], 'Password and Confirm password must match.')
            .required('Confirm Password is required.'),
    });
